/**
 * A button that stays busy until the page leaves (#32). `setLoading` is the starter's `Button`
 * `loading` prop ([enthused-starter#58](https://github.com/JonParton/enthused-starter/issues/58))
 * for a plain button: disabled, `aria-busy`, a spinner before its label that spins only where
 * motion is welcome, and the same width, so nothing jumps. A button may name its label element
 * (`data-slot="label"`) so the wording can move on while it waits.
 */

const SVG = "http://www.w3.org/2000/svg";

function spinner() {
  const svg = document.createElementNS(SVG, "svg");
  for (const [name, value] of Object.entries({
    "data-slot": "spinner",
    "aria-hidden": "true",
    width: "16",
    height: "16",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    "stroke-width": "2",
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    class: "size-4 shrink-0 motion-safe:animate-spin",
  }))
    svg.setAttribute(name, value);
  const path = document.createElementNS(SVG, "path");
  path.setAttribute("d", "M21 12a9 9 0 1 1-6.219-8.56");
  svg.append(path);
  return svg;
}

const labelOf = (control: HTMLElement) =>
  control.querySelector<HTMLElement>('[data-slot="label"]') ?? null;

export function setLoading(
  control: HTMLButtonElement | null | undefined,
  loading: boolean,
  label?: string,
) {
  if (!control) return;
  const text = labelOf(control);
  if (loading) {
    if (control.getAttribute("aria-busy") !== "true") {
      // CSSOM, not a style attribute, so the page's CSP (style-src 'self') allows it.
      control.style.minWidth = `${control.offsetWidth}px`;
      if (text && control.dataset.idleLabel === undefined)
        control.dataset.idleLabel = text.textContent ?? "";
      control.prepend(spinner());
    }
    control.disabled = true;
    control.setAttribute("aria-busy", "true");
    if (text && label) text.textContent = label;
    return;
  }
  control.disabled = false;
  control.removeAttribute("aria-busy");
  control.style.minWidth = "";
  control.querySelector('[data-slot="spinner"]')?.remove();
  if (text && control.dataset.idleLabel !== undefined) text.textContent = control.dataset.idleLabel;
  delete control.dataset.idleLabel;
}

/**
 * Runs `run` with `control` busy and labelled `label`. On success it stays busy: `run` has
 * started the navigation, and the button springs back only if the person comes back to this
 * page. On an error it resets, and the error goes on to the caller, which shows the message.
 */
export async function untilLeaving(
  control: HTMLButtonElement | null | undefined,
  label: string,
  run: () => Promise<void>,
) {
  if (control?.getAttribute("aria-busy") === "true") return;
  setLoading(control, true, label);
  try {
    await run();
  } catch (error) {
    setLoading(control, false);
    throw error;
  }
}

/**
 * Plain forms marked `data-busy` (the chooser, the account page): the pressed button goes busy
 * with its `data-loading-label` as the browser posts, and every other such button is disabled,
 * so a second press can't cancel the first.
 */
export function busyForms() {
  for (const form of document.querySelectorAll<HTMLFormElement>("form[data-busy]")) {
    form.addEventListener("submit", (event) => {
      const pressed =
        (event.submitter as HTMLButtonElement | null) ??
        form.querySelector<HTMLButtonElement>("button[type=submit]");
      if (pressed?.getAttribute("aria-busy") === "true") {
        event.preventDefault();
        return;
      }
      // After the browser has read the form, so disabling changes nothing it sends.
      setTimeout(() => {
        for (const other of document.querySelectorAll<HTMLButtonElement>(
          "form[data-busy] button[type=submit]",
        ))
          if (other !== pressed) other.disabled = true;
        setLoading(pressed, true, pressed?.dataset.loadingLabel);
      });
    });
  }
}

// Back from the next page (the back/forward cache keeps this one as it was): nothing is busy.
addEventListener("pageshow", (event) => {
  if (!event.persisted) return;
  for (const control of document.querySelectorAll<HTMLButtonElement>("button"))
    if (control.getAttribute("aria-busy") === "true" || control.closest("form[data-busy]"))
      setLoading(control, false);
});
